import { useState } from "react"

import { ArtifactBlock } from "@/components/artifact-block"
import { cn } from "@/lib/utils"

// Normalize what the user types into a bare host: drop scheme, "www.", any
// path/query, trailing slash. "https://www.yoursite.com/" -> "yoursite.com".
export function normalizeDomain(input: string): string {
  return input
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/.*$/, "")
    .trim()
}

const PLATFORMS = [
  "HTML",
  "WordPress",
  "Wix",
  "Squarespace",
  "Shopify",
] as const
type Platform = (typeof PLATFORMS)[number]

const SNIPPET_INSTRUCTIONS: Record<Platform, string> = {
  HTML: "Paste this snippet just before the closing </body> tag on every page.",
  WordPress:
    "Install a plugin like 'Insert Headers and Footers', then paste this into the footer section.",
  Wix: "Go to Settings → Custom Code and add a snippet to the Body - end section. Requires a Premium plan with a connected domain.",
  Squarespace:
    "Go to Settings → Advanced → Code Injection, and paste this into the Footer section. Available on Business and Commerce plans.",
  Shopify:
    "From your admin, open Online Store → Themes → Edit code, and paste this just before </body> in theme.liquid.",
}

// The one-line snippet points a <script src> at our per-agent loader, which
// injects our chat widget (in an iframe) configured from saved settings — so
// appearance/behavior changes take effect without re-pasting.
export function snippetFor(agentUid: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : ""
  return `<script src="${origin}/widget/${agentUid}.js" async></script>`
}

// The install snippet block — platform tabs, paste instructions, and a
// one-click copy. Shared by the setup wizard's Install step and the dashboard
// Edit drawer's Settings tab so the snippet stays findable after setup.
export function InstallSnippet({ agentUid }: { agentUid: string }) {
  const [platform, setPlatform] = useState<Platform>("HTML")
  const snippet = snippetFor(agentUid)

  return (
    <div className="space-y-3">
      <ArtifactBlock artifact={snippet} />

      <div className="flex flex-wrap gap-1.5">
        {PLATFORMS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPlatform(p)}
            className={cn(
              "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
              platform === p
                ? "border-foreground/20 bg-foreground text-background"
                : "border-border text-muted-foreground hover:bg-muted/40",
            )}
          >
            {p}
          </button>
        ))}
      </div>

      <p className="text-muted-foreground text-xs">
        {SNIPPET_INSTRUCTIONS[platform]}
      </p>
    </div>
  )
}
